import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";

const NAV_BASE =
  "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-extrabold transition-all";

function BrandMark() {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600/10 text-blue-700 ring-1 ring-blue-600/15">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2.5L19 6.3V12.2C19 16.9 15.9 20.8 12 22.1C8.1 20.8 5 16.9 5 12.2V6.3L12 2.5Z"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

function navClass(active) {
  return [
    NAV_BASE,
    active
      ? "bg-slate-900/5 text-slate-950 ring-1 ring-slate-900/10"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-950",
  ].join(" ");
}

export default function SalesAffiliateLayout({
  children,
  activeSection = "application",
}) {
  const navigate = useNavigate();
  const { configuration } = useSalesAffiliateWorkspace();

  const handleExit = async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {}

    navigate("/auth", { replace: true });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <aside className="sticky top-0 flex h-screen w-72 flex-shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-6 pb-6 pt-7">
          <div className="flex items-start gap-4">
            <BrandMark />
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.28em] text-blue-600">
                AI Fine Protector™
              </div>
              <div className="mt-2 text-xl font-black tracking-tight text-slate-950">
                Sales Affiliate
              </div>
              <div className="mt-1 text-xs leading-5 text-slate-500">
                Application and activation workspace
              </div>
            </div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-5">
          {configuration.navigation.map((group) => (
            <div key={group.section}>
              <div className="px-4 text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">
                {group.section}
              </div>

              <div className="mt-2 space-y-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      className={navClass(activeSection === item.key)}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-100 px-4 py-5">
          <button
            type="button"
            onClick={handleExit}
            className="flex items-center gap-3 px-4 py-2 text-sm font-bold text-slate-600 hover:text-red-700"
          >
            <LogOut size={18} />
            <span>Sign out</span>
          </button>

          <div className="mt-4 px-4 text-[11px] leading-snug text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
